import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  NbAuthOAuth2JWTToken,
  NbAuthService,
  NbAuthToken,
  NbTokenService,
} from '@nebular/auth';
import { BehaviorSubject, interval, map, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export const UserRoles = {
  SuperAdmin: 'super_admin',
  Admin: 'admin',
  Manager: 'manager',
  User: 'user',
};

export const UserAccess = {
  SideMenuAdmin: 'SideMenuAdmin',
  SideMenuCourses: 'SideMenuCourses',
  SideMenuDozent: 'SideMenuDozent',

  UserMenuOrganisation: 'UserMenuOrganisation',

  OrganisationEditUser: 'OrganisationEditUser',
  OrganisationDelete: 'OrganisationDelete',
  OrganisationManageSubscription: 'OrganisationManageSubscription',

  UsageEditApiKey: 'UsageEditApiKey',

  UserDialogSuperAdminRole: 'UserDialogSuperAdminRole',

  DozentFaqs: 'DozentFaqs',
  DozentParticipants: 'DozentParticipants',
  DozentExercises: 'DozentExercises',
  DozentAssignments: 'DozentAssignments',
  DozentAnalytics: 'DozentAnalytics',
  DozentMessages: 'DozentMessages',
};

export const UserRoleMapping: Record<string, string[]> = {
  [UserRoles.SuperAdmin]: [...Object.values(UserAccess)],
  [UserRoles.Admin]: [
    UserAccess.UserMenuOrganisation,
    UserAccess.OrganisationEditUser,
    UserAccess.OrganisationManageSubscription,
    UserAccess.UsageEditApiKey,
    UserAccess.OrganisationDelete,
    UserAccess.SideMenuDozent,
    UserAccess.DozentFaqs,
    UserAccess.DozentParticipants,
    UserAccess.DozentExercises,
    UserAccess.DozentAssignments,
    UserAccess.DozentAnalytics,
    UserAccess.DozentMessages,
  ],
  [UserRoles.Manager]: [
    UserAccess.UserMenuOrganisation,
    UserAccess.UsageEditApiKey,
    UserAccess.SideMenuDozent,
    UserAccess.DozentFaqs,
    UserAccess.DozentParticipants,
    UserAccess.DozentExercises,
    UserAccess.DozentAssignments,
    UserAccess.DozentAnalytics,
    UserAccess.DozentMessages,
  ],
  [UserRoles.User]: [],
};

export interface IAuthTokens {
  access_token: string;
  refresh_token: string;
}

export interface AccessTokenPayload {
  client_id: string;
  user_id: string;
  company_name: string | null;
  current_plan: number;
  email: string;
  exp: number;
  name: string;
  role: string;
  sub: string;
  impersonating: boolean;
  feature_set_permissions: string[];
  /** Org-feature gate for the Moodle dashboard sidebar entry.
   *  Resolved at login from `'MOODLE_DASHBOARD' in client.features`. */
  moodle_dashboard_enabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  accessToken$ = new BehaviorSubject<string | undefined>(undefined);
  accessTokenPayload$ = new BehaviorSubject<AccessTokenPayload | undefined>(
    undefined
  );

  UserAccess = UserAccess;

  constructor(
    protected nbAuthService: NbAuthService,
    protected router: Router,
    protected tokenService: NbTokenService,
    protected http: HttpClient,
    protected stateCleanupService: StateCleanupService
  ) {
    this.nbAuthService.onTokenChange().subscribe((token) => {
      this.updateTokenState(token);
    });

    // Refresh token every 5 minutes
    interval(1000 * 60 * 5).subscribe(() => {
      if (!this.accessToken$.value) {
        return;
      }

      this.refreshToken().subscribe((res) => {
        console.log('refreshed token', res);
      });
    });
  }

  public impersonate(client_id: any) {
    return this.http
      .post(`${environment.userBackend}/admin/impersonate/${client_id}`, {})
      .pipe(
        map((d: any) => d.data),
        tap((d: { access_token: string; refresh_token: string }) => {
          const { access_token, refresh_token } = d;
          const token = {
            access_token,
            refresh_token,
            token_type: 'bearer',
          };

          const currentLocalStorageToken = JSON.parse(
            localStorage.getItem('auth_app_token')!
          );

          currentLocalStorageToken.value = token;

          localStorage.setItem(
            'auth_app_token',
            JSON.stringify(currentLocalStorageToken)
          );

          this.nbAuthService.getToken().subscribe((token) => {
            console.log('Token', token);
          });
        })
      );
  }

  public stopImpersonate() {
    return this.http
      .post(`${environment.userBackend}/admin/exit-impersonation`, {})
      .pipe(
        tap((d: any) => {
          console.log('Impersonation stopped', d);
          this.logout();
          localStorage.removeItem('auth_app_token');
          window.location.reload();
        })
      );
  }

  /**
   * True only for the super_admin role — deliberately NOT routed through
   * hasAccess/feature sets. Used to hide features that are in limited testing
   * (currently the credit purchase card on the Organisation page). The backend
   * enforces the same gate; this only keeps the UI out of everyone else's way.
   */
  public isSuperAdmin(): boolean {
    return this.accessTokenPayload$.value?.role === UserRoles.SuperAdmin;
  }

  /**
   * True for roles with clientwide visibility (admin / super_admin /
   * manager) — mirrors the backend `CLIENT_WIDE_ROLES`. A role='user'
   * dozent (feature set {DOZENT}) is NOT clientwide: they see only their
   * own bots and must request cross-agent actions (e.g. twin↔tutor links)
   * from an admin/manager.
   */
  public isClientWide(): boolean {
    const role = this.accessTokenPayload$.value?.role;
    return (
      role === UserRoles.SuperAdmin ||
      role === UserRoles.Admin ||
      role === UserRoles.Manager
    );
  }

  /**
   * True for admin and super_admin only — deliberately NOT `manager`.
   * Guards the tenant-wide second-factor switch and the reset of someone
   * else's factor: those are account-security controls, not content
   * permissions, and the backend draws the same line (`ADMIN_ROLES` in
   * `twofa_endpoints.py`).
   */
  public isAdmin(): boolean {
    const role = this.accessTokenPayload$.value?.role;
    return role === UserRoles.SuperAdmin || role === UserRoles.Admin;
  }


  public hasAccess(userAccess: string): boolean {
    const payload = this.accessTokenPayload$.value;
    if (!payload) return false;

    // Feature set OVERRIDES role permissions when present
    const featurePerms = payload.feature_set_permissions || [];
    if (featurePerms.length > 0) {
      return featurePerms.includes(userAccess);
    }

    // No feature set: standard role-based permissions
    return UserRoleMapping[payload.role]?.includes(userAccess) ?? false;
  }

  /**
   * Check if user has access to an org-level feature (DOZENT, ANALYTICS, CONTENT_GENERATOR).
   * Without feature set: true (admins/managers see everything).
   * With feature set: only if the feature is in the set.
   */
  public hasFeatureAccess(feature: string): boolean {
    const payload = this.accessTokenPayload$.value;
    if (!payload) return false;
    const featurePerms = payload.feature_set_permissions || [];
    if (featurePerms.length === 0) return true;
    return featurePerms.includes(feature);
  }

  get nbAuth() {
    return this.nbAuthService;
  }

  logout() {
    this.tokenService.clear();
    this.stateCleanupService.clearAll();
    this.accessToken$.next(undefined);
    this.accessTokenPayload$.next(undefined);
    this.router.navigate(['/auth/login']);
  }

  getAccessTokenPayload(): AccessTokenPayload {
    return this.accessTokenPayload$.value!;
  }

  confirmRegistration(token: string) {
    return this.http.post(`${environment.userBackend}/confirm-registration`, {
      token,
    });
  }

  requestPasswordReset(email: string) {
    return this.http.post(`${environment.userBackend}/password/reset`, {
      email,
    });
  }

  confirmPasswordReset(token: string, password: string, confirmPassword: string) {
    return this.http.post(
      `${environment.userBackend}/password/reset/confirm`,
      {
        reset_password_token: token,
        password,
        confirmPassword,
      },
      { observe: 'response' }
    );
  }

  resendConfirmation(email: string) {
    return this.http.post(`${environment.userBackend}/resend-confirmation`, {
      email,
    });
  }

  confirmEmailChange(token: string) {
    return this.http.post(`${environment.userBackend}/email/change-confirm`, {
      token,
    });
  }

  refreshToken() {
    return this.nbAuth.getToken().pipe(
      switchMap((token) => {
        return this.nbAuth.refreshToken('email', token);
      })
    );
  }

  private updateTokenState(token: NbAuthToken | null) {
    if (!token || !(token instanceof NbAuthOAuth2JWTToken)) {
      this.accessToken$.next(undefined);
      this.accessTokenPayload$.next(undefined);
      return;
    }

    const accessToken =
      (token as any)?.token?.access_token ?? token.getValue() ?? undefined;

    if (!accessToken) {
      this.accessToken$.next(undefined);
      this.accessTokenPayload$.next(undefined);
      return;
    }

    this.accessToken$.next(accessToken);

    const payload = token.getAccessTokenPayload() as AccessTokenPayload;

    if (payload.role === undefined) {
      payload.role = UserRoles.User;
    }

    this.accessTokenPayload$.next(payload);
  }
}
